import "../styling/PaymentMethodsStyles.css";
import { devLog } from "../../../utils/devLog";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useStyles } from "../../../context/StyleContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCheckout } from "../../../context/CheckoutContext";
import { toast } from "react-toastify";
import { createCheckoutComPaymentSession, fetchCheckoutDetails, resolveCheckoutAmountMinor } from "../../../api/api";
import { mapStylesToCheckoutFlow, createCheckoutComComponentOptions } from "../../../utils/checkoutComStyleMapper";
import { useCheckoutCard } from "../../../hooks/useCheckoutCard";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import {
    handleCitPaymentApiResponse,
    applyCitDeclineOrErrorUi,
    CIT_OUTCOME,
    getShopperCitFailureMessage,
} from "../../../utils/paymentOutcome";
import CheckoutComCardFormView from "./CheckoutComCardFormView";
import useWebSocket from "../../../hooks/useWebSocket";

const CheckoutComCardForm = ({
    formData,
    itemDetails,
    billingPlatformId,
    routingEntries,
    planId,
    platformItemDetails,
    selectedAddons,
    prefetchedGatewayConnection,
    sessionId,
    checkoutProcessDto,
    checkoutPageConfDto,

    // Hooks for Altruon variant
    isEmbeddedCheckout = false,
    onParentCallback
}) => {
    const { t } = useTranslation();
    const { styles } = useStyles();
    const {
        triggerPaymentMethodEvent,
        resetTrigger,
        clientIp,
        clientCountry,
        paymentMethodName,
        platformName,
        estimatedOrder,
        isHostedPageMode,
        citPaymentErrorMessage,
    } = useCheckout();

    // Local state
    const [cardholderName, setCardholderName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [gatewayConnection, setGatewayConnection] = useState(null);
    const [cardBrand, setCardBrand] = useState(null);
    const [paymentSession, setPaymentSession] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [transactionStatus, setTransactionStatus] = useState("");
    const [checkoutConfDto, setCheckoutConfDto] = useState(
        checkoutPageConfDto || null
    );
    const [showProcessingModal, setProcessingModal] = useState(false);
    const [transactionId, setTransactionId] = useState(null);
    const [hostedPageRedirect, setHostedPageRedirect] = useState(null);

    const cardElementRef = useRef(null);
    const saveCard = true;
    const isProcessingRef = useRef(false);
    const mountTimeoutRef = useRef(null);
    const paymentSessionPayloadRef = useRef({
        itemDetails,
        formData,
        clientCountry,
        platformItemDetails,
        selectedAddons,
        estimatedOrder,
    });
    const prefetchedGatewayConnectionRef = useRef(prefetchedGatewayConnection);
    const tRef = useRef(t);
    /** Amount (minor units) used for the current Checkout.com payment-session. */
    const paymentSessionAmountRef = useRef(null);
    const paymentSessionRefreshInFlightRef = useRef(false);

    // determine environment from gateway connection or use sandbox default
    const isSandbox = true;
    const cardCheckoutcomRouting = useMemo(() => {
        return routingEntries?.find(
            (entry) => entry.paymentMethod === "card" && entry.platform === "checkoutcom"
        );
    }, [routingEntries]);
    const checkoutcomRoutingId = cardCheckoutcomRouting?.id ?? null;

    useEffect(() => {
        paymentSessionPayloadRef.current = {
            itemDetails,
            formData,
            clientCountry,
            platformItemDetails,
            selectedAddons,
            estimatedOrder,
        };
    }, [
        itemDetails,
        formData,
        clientCountry,
        platformItemDetails,
        selectedAddons,
        estimatedOrder,
    ]);

    useEffect(() => {
        prefetchedGatewayConnectionRef.current = prefetchedGatewayConnection;
    }, [prefetchedGatewayConnection]);

    useEffect(() => {
        tRef.current = t;
    }, [t]);

    useEffect(() => {
        if (checkoutPageConfDto) {
            setCheckoutConfDto(checkoutPageConfDto);
            return;
        }
        if (billingPlatformId && planId) {
            loadCheckoutDetails();
        }
    }, [billingPlatformId, planId, checkoutPageConfDto]);

    useEffect(() => {
        if (!citPaymentErrorMessage) return;
        setError(citPaymentErrorMessage);
        setLoading(false);
        isProcessingRef.current = false;
        setProcessingModal(false);
    }, [citPaymentErrorMessage]);

    const loadCheckoutDetails = async () => {
        try {
            const data = await fetchCheckoutDetails(billingPlatformId, planId);

            if (data?.checkoutPageConfDto) {
                setCheckoutConfDto(data.checkoutPageConfDto);
            }
        } catch (error) {
            toast.error(t("messages.error.somethingWrong") || "Something went wrong.");
        }
    };


    // Initialize gateway connection & payment session once when session + routing
    // are ready. Do not depend on t/formData/estimatedOrder/gatewayConnection —
    // those were re-firing this effect (duplicate gatewayConnection + init-session).
    useEffect(() => {
        if (!sessionId || !checkoutcomRoutingId) return;

        let cancelled = false;

        const initialize = async () => {
            try {
                let connection = prefetchedGatewayConnectionRef.current;
                if (!connection) {
                    connection = await fetchGatewayConnection(
                        { id: checkoutcomRoutingId },
                        tRef.current
                    );
                }
                if (cancelled) return;
                if (connection) setGatewayConnection(connection);

                const payload = paymentSessionPayloadRef.current;
                paymentSessionAmountRef.current = resolveCheckoutAmountMinor(payload);
                const session = await createCheckoutComPaymentSession({
                    sessionId,
                    ...payload,
                });
                if (cancelled) return;
                if (session) setPaymentSession(session);
            } catch (err) {
                if (cancelled) return;
                console.error("Failed to initialize Checkout.com:", err);
                setError(tRef.current("messages.error.somethingWrong"));
            }
        };

        initialize();
        return () => {
            cancelled = true;
        };
    }, [sessionId, checkoutcomRoutingId]);

    // If the form mounted before estimate finished, refresh payment-session once
    // when the estimate amount differs — without re-running on every keystroke.
    useEffect(() => {
        if (!sessionId || !checkoutcomRoutingId || !paymentSession || !estimatedOrder) {
            return;
        }
        if (paymentSessionRefreshInFlightRef.current) return;

        const nextAmount = resolveCheckoutAmountMinor(paymentSessionPayloadRef.current);
        if (
            nextAmount == null ||
            nextAmount === paymentSessionAmountRef.current
        ) {
            return;
        }

        let cancelled = false;
        paymentSessionRefreshInFlightRef.current = true;

        createCheckoutComPaymentSession({
            sessionId,
            ...paymentSessionPayloadRef.current,
        })
            .then((session) => {
                if (cancelled || !session) return;
                paymentSessionAmountRef.current = nextAmount;
                setPaymentSession(session);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Failed to refresh Checkout.com payment session:", err);
            })
            .finally(() => {
                paymentSessionRefreshInFlightRef.current = false;
            });

        return () => {
            cancelled = true;
        };
    }, [estimatedOrder, sessionId, checkoutcomRoutingId, paymentSession]);

    // Checkout.com Card SDK hook
    const {
        cardComponent,
        ready: cardReady,
        cardMetadata,
        error: cardError,
        loading: sdkLoading,
        mount,
        tokenize,
        clearError,
    } = useCheckoutCard({
        publicKey: gatewayConnection?.credentials?.public_key,
        paymentSession,
        environment: isSandbox ? "sandbox" : "production",
        appearance: mapStylesToCheckoutFlow(styles),
        componentOptions: createCheckoutComComponentOptions({
            cardholderName: formData?.firstName && formData?.lastName
                ? `${formData.firstName} ${formData.lastName}`
                : '',
        }),
        onReady: () => {
            devLog("Checkout.com Card ready");
        },
        onError: (err) => {
            console.error("Card error:", err);
            setError(err?.message || err);
        },
        onBinChanged: (metadata) => {
            setCardBrand(metadata?.scheme);
        },
    });

    const validateCardState = () => {
        if (!cardComponent || !cardReady) {
            return t("paymentForms.card.notReady") || "Card form not ready";
        }
        if (cardError?.toLowerCase().match(/initialization|failed to load|not available/)) {
            return cardError;
        }
        return null;
    };

    const buildPayload = (token, gatewayConnection, latestSession) => ({
        paymentData: {
            gatewayConnectionId: gatewayConnection.id,
            gatewayName: gatewayConnection.platform,
            paymentMethod: "card",
            // amount: itemDetails?.price || latestSession?.paymentData?.amount,
            // currency: itemDetails?.currencyCode || latestSession?.paymentData?.currency || "USD",
            transactionId: "",
            additionalData: {
                payment_method_flow: "DIRECT",
                cardId: token,
                // Session additionalData is merged key by key; explicit nulls drop a token
                // intent left by an earlier Altruon vault attempt in this session.
                vault: null,
                tokenIntentId: null,
                cardholder_name: cardholderName.trim(),
                card_brand: cardBrand,
                save: saveCard,
                capture: true,
                verify: false,
            },
        },
        customerData: {
            firstName: formData?.firstName || latestSession?.customerData?.firstName,
            lastName: formData?.lastName || latestSession?.customerData?.lastName,
            email: formData?.email || latestSession?.customerData?.email,
            phone: formData?.phone || latestSession?.customerData?.phone || "9908734801",
            ip: clientIp || "127.0.0.1",
            billingAddress: {
                country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || clientCountry || "US",
                zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210",
                street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
                city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
                state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
                number: "1234",
            },
        },
        billingData: {
            planId: planId,
            billingPlatformId,
            billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
            frequency: latestSession?.billingData?.frequency || "",
        }
    });


    const handleProcessPayment = async (payload, token) => {
        if (isEmbeddedCheckout) {
            onParentCallback?.({
                type: "finalizeFormData",
                data: {
                    paymentMethod: "card",
                    gatewayName: "checkoutcom",
                    cardId: token,
                    cardToken: token,
                    cardBrand: cardBrand,
                    saveCard: true,
                },
            });
            await new Promise((r) => setTimeout(r, 800));
        }

        if (!isEmbeddedCheckout) {
            await updateSession(sessionId, payload);
        }

        return processPayment(billingPlatformId, sessionId, payload);
    };

    // Primary pay handler
    const handlePayNow = async () => {
        if (isProcessingRef.current) {
            console.warn("Payment already in progress");
            return;
        }
        isProcessingRef.current = true;
        if (!isEmbeddedCheckout) {
            setProcessingModal(true);
        }
        clearError && clearError();

        if (!cardCheckoutcomRouting) {
            setError(t("paymentForms.card.unavailable"));
            setProcessingModal(false);
            isProcessingRef.current = false;
            return;
        }

        setError("");

        const validationError = validateCardState();
        if (validationError) {
            setError(validationError);
            isProcessingRef.current = false;
            return;
        }
        setLoading(true);

        try {
            // Tokenize via SDK
            const tokenResult = await tokenize();
            const token = tokenResult?.data?.token || tokenResult?.token || tokenResult;
            const cardMeta = tokenResult?.cardMetadata || tokenResult?.data?.cardMetadata || cardMetadata;

            if (!token) {
                throw new Error(t("paymentForms.card.tokenError"));
            }

            const gateway_connection = gatewayConnection || await fetchGatewayConnection(cardCheckoutcomRouting, t);
            const latestSession = await fetchSession(sessionId);
            // await new Promise(resolve => setTimeout(resolve, 800));
            const payload = buildPayload(token, gateway_connection, latestSession);
            if (!payload) {
                setLoading(false);
                return;
            }
            const res = await handleProcessPayment(payload, token);

            const citUi = {
                isHostedPageMode,
                isEmbeddedCheckout,
                t,
                setError,
                setTransactionStatus,
                setShowStatusModal,
                onParentCallback,
                toast,
            };
            const { handled } = handleCitPaymentApiResponse(res, citUi);
            if (handled) return;

            if (res.status === 200) {
                if (res?.data?.hosted_redirect_page) {
                    setHostedPageRedirect(res.data.hosted_redirect_page);
                }
                const redirectUrl = res.data.redirect_url && `${res.data.redirect_url}?trxId=${res.data.transactionId}`;
                const requiresRedirect = Boolean(res?.data?.on_redirect_required && redirectUrl);

                setTransactionId(res?.data?.newTransactionId || res?.data?.transactionId);
                if (isEmbeddedCheckout) {
                    if (redirectUrl) {
                        onParentCallback?.({
                            type: "actionRequired",
                            action: "REDIRECT",
                            url: redirectUrl,
                        });
                    } else {
                        onParentCallback?.({
                            type: "success",
                            transactionId: res.data.transactionId,
                        });
                    }
                } else {
                    if (requiresRedirect) {
                        safeRedirect(redirectUrl, { target: "self" });
                        return;
                    }
                    setTransactionStatus("success");
                    setShowStatusModal(true);
                    // toast.info(t("messages.info.redirecting"));
                }
            } else {
                applyCitDeclineOrErrorUi(
                    { outcome: CIT_OUTCOME.ERROR, message: t("messages.error.somethingWrong") },
                    citUi
                );
                throw new Error(t("messages.error.somethingWrong"));
            }
        } catch (e) {
            const citUi = {
                isHostedPageMode,
                isEmbeddedCheckout,
                t,
                setError,
                setTransactionStatus,
                setShowStatusModal,
                onParentCallback,
                toast,
            };
            if (e?.response && handleCitPaymentApiResponse(e.response, citUi).handled) return;
            applyCitDeclineOrErrorUi(
                { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(e?.message, t) },
                citUi
            );
        } finally {
            setLoading(false);
            isProcessingRef.current = false;
            if (!isEmbeddedCheckout) {
                setProcessingModal(false);
            }
        }
    };


    // Trigger payment method event from CheckoutContext
    useEffect(() => {
        if (triggerPaymentMethodEvent &&
            (isEmbeddedCheckout || (platformName === "checkoutcom" && paymentMethodName === "card"))) {
            handlePayNow();
            resetTrigger();
        }
    }, [
        triggerPaymentMethodEvent,
        paymentMethodName,
        platformName,
        resetTrigger,
        handlePayNow,
    ]);


    const isCheckoutCardMounted = useCallback((element) => {
        if (!element) return false;
        return Boolean(element.querySelector("iframe"));
    }, []);

    useEffect(() => {
        // Clear any pending mount timeout
        if (mountTimeoutRef.current) {
            clearTimeout(mountTimeoutRef.current);
            mountTimeoutRef.current = null;
        }

        if (!gatewayConnection?.credentials?.public_key) {
            return;
        }

        if (!paymentSession) {
            return;
        }

        if (!cardComponent) {
            return;
        }

        if (!cardElementRef.current) {
            return;
        }

        if (isCheckoutCardMounted(cardElementRef.current)) {
            return;
        }

        mountTimeoutRef.current = setTimeout(async () => {
            try {
                if (!cardElementRef.current || isCheckoutCardMounted(cardElementRef.current)) {
                    return;
                }

                await mount(cardElementRef.current);
                mountTimeoutRef.current = null;
            } catch (err) {
                console.error('❌ Failed to mount card:', err);
                setError(err.message);
                mountTimeoutRef.current = null;
            }
        }, 300);

        // Cleanup: Clear timeout if effect re-runs or component unmounts
        return () => {
            if (mountTimeoutRef.current) {
                clearTimeout(mountTimeoutRef.current);
                mountTimeoutRef.current = null;
            }
        };
    }, [gatewayConnection, paymentSession, cardComponent, mount, sdkLoading, isCheckoutCardMounted]);

    // Listen websocket only for SDK version
    useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
        const data = JSON.parse(event.data);

        if (!data?.status) return;

        if (data.status === "success") {
            onParentCallback?.({
                type: "success",
                transactionId,
            });
        }

        if (data.status === "failed") {
            onParentCallback?.({
                type: "failure",
            });
        }
    },
        undefined,
        undefined,
        (err) => {
            console.error("CARD WS error", err);
        }
    );

    return (
        <CheckoutComCardFormView
            t={t}
            loading={loading}
            error={error}
            cardError={cardError}
            sdkLoading={sdkLoading}
            cardReady={cardReady}
            cardElementRef={cardElementRef}
            showProcessingModal={showProcessingModal}
            setProcessingModal={setProcessingModal}
            showStatusModal={showStatusModal}
            setShowStatusModal={setShowStatusModal}
            transactionStatus={transactionStatus}
            checkoutConfDto={checkoutConfDto}
            transactionId={transactionId}
            hostedPageRedirect={hostedPageRedirect}
        />
    );
};

export default CheckoutComCardForm;